import { useEffect, useRef, useState } from "react";
import { ListChecks } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { usePlan, useArchivePlan, useEditPlan, useProjectPlans } from "../../hooks/usePlan";
import { errorMessage } from "../../hooks/useServers";

const ICON = { pendiente: "⏸️", en_curso: "▶", hecho: "✅", bloqueado: "❌" };
const STATE_LABEL = { pendiente: "Pendiente", en_curso: "En curso", hecho: "Hecho", bloqueado: "Bloqueado" };

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

const MAX_STEPS = 12;
const MAX_EVIDENCE = 3;
const EVID_ICON = { enlace: "🔗", commit: "🔖", captura: "🖼️", nota: "📝" };
const EVID_KIND = { enlace: "Enlace", commit: "Commit", captura: "Captura (enlace)", nota: "Nota" };
const EVID_HINT = { enlace: "https://…", commit: "Código, ej. 6cf39fd", captura: "https://… (enlace a la imagen)", nota: "Nota corta" };
const isUrl = (v) => /^https?:\/\/\S+$/.test(v || "");
const hostOf = (v) => { try { return new URL(v).host; } catch { return v; } };

// Una prueba de que el paso se hizo: enlace, commit, captura (enlace a la imagen) o nota. Con `onRemove` (modo edición) se puede quitar.
function EvidenceChip({ e, testId, onRemove, disabled }) {
  const text = e.kind === "commit" ? `commit ${e.value.slice(0, 7)}` : e.kind === "nota" ? e.value : e.label || (e.kind === "captura" ? "Captura" : hostOf(e.value));
  const linked = (e.kind === "enlace" || e.kind === "captura") && isUrl(e.value);
  return (
    <span data-testid={testId} data-kind={e.kind} className="inline-flex max-w-full items-center gap-1 rounded-full bg-foreground/10 px-2 py-0.5 text-xs">
      {linked ? (
        <a href={e.value} target="_blank" rel="noopener noreferrer" title={e.value} className="underline truncate">{EVID_ICON[e.kind]} {text}</a>
      ) : (
        <span title={e.kind === "commit" ? e.value : undefined} className="truncate">{EVID_ICON[e.kind]} {text}</span>
      )}
      {onRemove && <button type="button" data-testid={`${testId}-remove`} aria-label="Quitar esta evidencia" title="Quitar" disabled={disabled} className="rounded hover:bg-foreground/20" onClick={onRemove}>✕</button>}
    </span>
  );
}
const NEXT_STATE = { pendiente: "en_curso", en_curso: "hecho", hecho: "bloqueado", bloqueado: "pendiente" };

// Un paso. Con `controls` (solo el plan vigente) el ícono cambia su estado con un clic y, en modo edición, se puede cambiar el texto, mover y quitar.
function StepRow({ step: s, index: i, last, testPrefix, controls, showMissing }) {
  const [draft, setDraft] = useState(s.text);
  const [removing, setRemoving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState("enlace");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  useEffect(() => setDraft(s.text), [s.text]);
  const live = !!controls && !controls.working; // mientras la IA trabaja el plan es de solo lectura
  const editing = live && controls.editing;
  const saveText = () => {
    const text = draft.trim();
    if (!text) setDraft(s.text);
    else if (text !== s.text) controls.onText(s, text);
  };
  const evidence = s.evidence || [];
  const saveEvidence = async () => {
    if (!value.trim()) return;
    if (await controls.onEvidenceAdd(s, { kind, value: value.trim(), label: label.trim() })) { setAdding(false); setValue(""); setLabel(""); }
  };
  return (
    <li data-testid={`${testPrefix}-step-${i}`} data-state={s.state} className="flex items-start gap-2 text-sm">
      {live ? (
        <button type="button" data-testid={`${testPrefix}-state-${i}`} disabled={controls.busy} aria-label={`${STATE_LABEL[s.state]}. Pulsa para cambiar el estado de este paso`}
          title="Pulsa para cambiar el estado" onClick={() => controls.onState(s, NEXT_STATE[s.state] || "pendiente")} className="shrink-0 w-5 text-center rounded hover:bg-foreground/10">
          {ICON[s.state] || ICON.pendiente}
        </button>
      ) : (
        <span aria-label={STATE_LABEL[s.state]} className="shrink-0 w-5 text-center">{ICON[s.state] || ICON.pendiente}</span>
      )}
      <div className="min-w-0 flex-1">
        {editing ? (
          <Input data-testid={`${testPrefix}-text-${i}`} value={draft} maxLength={120} disabled={controls.busy} className="h-8 text-sm" aria-label="Texto del paso"
            onChange={(e) => setDraft(e.target.value)} onBlur={saveText} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
        ) : (
          <p className={s.state === "hecho" ? "text-foreground/60 line-through" : s.state === "en_curso" ? "font-medium text-foreground" : "text-foreground/85"}>{s.text}</p>
        )}
        {s.note && <p data-testid={`${testPrefix}-note-${i}`} className="text-xs text-muted-foreground">{s.note}</p>}
        {(evidence.length > 0 || (showMissing && s.state === "hecho")) && (
          <div data-testid={`${testPrefix}-evidence-${i}`} className="mt-1 flex flex-wrap items-center gap-1">
            {evidence.map((e, j) => (
              <EvidenceChip key={e.id} e={e} testId={`${testPrefix}-evid-${i}-${j}`} disabled={controls?.busy} onRemove={editing ? () => controls.onEvidenceRemove(s, e) : null} />
            ))}
            {showMissing && s.state === "hecho" && evidence.length === 0 && <span data-testid={`${testPrefix}-noevid-${i}`} className="text-xs text-amber-500">⚠ sin evidencia</span>}
          </div>
        )}
        {editing && evidence.length < MAX_EVIDENCE && !adding && (
          <button type="button" data-testid={`${testPrefix}-evid-add-${i}`} disabled={controls.busy} className="mt-1 text-xs underline text-muted-foreground" onClick={() => setAdding(true)}>＋ Evidencia</button>
        )}
        {editing && adding && (
          <div data-testid={`${testPrefix}-evid-form-${i}`} className="mt-1 space-y-1 rounded-lg border border-border p-2">
            <div className="flex items-center gap-1">
              <select data-testid={`${testPrefix}-evid-kind-${i}`} value={kind} aria-label="Tipo de evidencia" disabled={controls.busy} className="h-8 rounded-md border border-input bg-background px-1 text-xs" onChange={(e) => setKind(e.target.value)}>
                {Object.keys(EVID_KIND).map((k) => <option key={k} value={k}>{EVID_ICON[k]} {EVID_KIND[k]}</option>)}
              </select>
              <Input data-testid={`${testPrefix}-evid-value-${i}`} value={value} maxLength={300} disabled={controls.busy} placeholder={EVID_HINT[kind]} aria-label="Valor de la evidencia" className="h-8 text-xs"
                onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEvidence(); }} />
            </div>
            {(kind === "enlace" || kind === "captura") && (
              <Input data-testid={`${testPrefix}-evid-label-${i}`} value={label} maxLength={40} disabled={controls.busy} placeholder="Nombre corto (opcional)" aria-label="Nombre de la evidencia" className="h-8 text-xs"
                onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEvidence(); }} />
            )}
            <div className="flex gap-2 text-xs">
              <button type="button" data-testid={`${testPrefix}-evid-save-${i}`} disabled={controls.busy || !value.trim()} className="underline" onClick={saveEvidence}>Guardar</button>
              <button type="button" data-testid={`${testPrefix}-evid-cancel-${i}`} className="underline text-muted-foreground" onClick={() => { setAdding(false); setValue(""); setLabel(""); }}>Cancelar</button>
            </div>
          </div>
        )}
        {editing && removing && (
          <div data-testid={`${testPrefix}-remove-confirm-${i}`} className="mt-1 flex items-center gap-2 text-xs">
            <span>¿Quitar este paso?</span>
            <button type="button" data-testid={`${testPrefix}-remove-yes-${i}`} disabled={controls.busy} className="underline" onClick={() => { setRemoving(false); controls.onRemove(s); }}>Sí, quitar</button>
            <button type="button" data-testid={`${testPrefix}-remove-no-${i}`} className="underline text-muted-foreground" onClick={() => setRemoving(false)}>No</button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="shrink-0 flex items-center gap-0.5">
          <button type="button" data-testid={`${testPrefix}-up-${i}`} aria-label="Subir este paso" title="Subir" disabled={controls.busy || i === 0} className="px-1 rounded hover:bg-foreground/10 disabled:opacity-30" onClick={() => controls.onMove(s, "arriba")}>↑</button>
          <button type="button" data-testid={`${testPrefix}-down-${i}`} aria-label="Bajar este paso" title="Bajar" disabled={controls.busy || last} className="px-1 rounded hover:bg-foreground/10 disabled:opacity-30" onClick={() => controls.onMove(s, "abajo")}>↓</button>
          <button type="button" data-testid={`${testPrefix}-remove-${i}`} aria-label="Quitar este paso" title="Quitar" disabled={controls.busy} className="px-1 rounded hover:bg-foreground/10" onClick={() => setRemoving(true)}>🗑️</button>
        </div>
      ) : (
        s.state === "hecho" && s.updated_at && <span className="shrink-0 text-xs text-muted-foreground">{fmtDate(s.updated_at)}</span>
      )}
    </li>
  );
}

function Steps({ steps, testPrefix, controls, showMissing = false }) {
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => <StepRow key={s.id} step={s} index={i} last={i === steps.length - 1} testPrefix={testPrefix} controls={controls} showMissing={showMissing} />)}
    </ol>
  );
}

// «📋 Plan»: el plan de ESTA conversación (o tema) con su avance, que no se pierde cuando el chat crece. Se actualiza solo mientras la IA trabaja.
export function PlanButton({ conversationId, projectId = null, onOpenConversation = null, watch = false }) {
  const [open, setOpen] = useState(false);
  const { data, refetch } = usePlan(conversationId, watch);
  const watching = useRef(false);
  useEffect(() => {
    if (watching.current && !watch && conversationId) refetch(); // al terminar la respuesta, una última lectura
    watching.current = watch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);
  const current = data?.current;
  const badge = current ? (current.status === "done" ? "✅" : `${current.done}/${current.total}`) : null;
  return (
    <>
      <Button data-testid="plan-button" size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!conversationId}>
        <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Plan
        {badge && <span data-testid="plan-badge" className="ml-1.5 rounded-full bg-foreground/10 px-1.5 text-[11px] tabular-nums">{badge}</span>}
        {current?.stale_days ? <span data-testid="plan-stale-dot" title="Sin movimiento desde hace días" className="ml-1 h-2 w-2 rounded-full bg-amber-500" /> : null}
      </Button>
      <PlanDialog open={open} onOpenChange={setOpen} conversationId={conversationId} data={data} working={watch} projectId={projectId} onOpenConversation={onOpenConversation} />
    </>
  );
}

// «📚 Todos los planes del proyecto»: el plan vigente de cada conversación y tema, con su avance. Solo lectura: para editar uno se va a su conversación.
function ProjectPlans({ projectId, conversationId, onOpenConversation, onClose }) {
  const { data, isLoading, isError } = useProjectPlans(projectId);
  const [openId, setOpenId] = useState(null);
  const items = data?.plans || [];
  const summary = data?.summary;
  const parts = summary ? [
    summary.active ? `${summary.active} en curso` : null,
    summary.done ? `${summary.done} completado${summary.done === 1 ? "" : "s"}` : null,
    summary.stale ? `${summary.stale} sin movimiento` : null,
  ].filter(Boolean) : [];
  return (
    <div data-testid="project-plans" className="space-y-3">
      {isLoading && <p data-testid="project-plans-loading" className="text-sm text-muted-foreground">Cargando los planes del proyecto…</p>}
      {isError && <p data-testid="project-plans-error" className="text-sm text-amber-500">No se pudieron leer los planes del proyecto.</p>}
      {summary && items.length === 0 && (
        <p data-testid="project-plans-empty" className="text-sm text-muted-foreground">Todavía no hay planes guardados en este proyecto. Aparecen aquí cuando el Orquestador propone o acuerda uno en cualquier conversación o tema.</p>
      )}
      {summary && items.length > 0 && (
        <p data-testid="project-plans-summary" className="text-sm font-medium">{summary.total} plan{summary.total === 1 ? "" : "es"}: {parts.join(" · ")}</p>
      )}
      <ul className="space-y-2">
        {items.map((item) => {
          const plan = item.plan;
          const here = item.conversation_id === conversationId;
          const open = openId === item.conversation_id;
          return (
            <li key={item.conversation_id} data-testid={`project-plan-${item.conversation_id}`} data-status={plan.status} className="rounded-lg border border-border p-3 space-y-2">
              <button type="button" data-testid={`project-plan-toggle-${item.conversation_id}`} className="w-full text-left space-y-1" onClick={() => setOpenId(open ? null : item.conversation_id)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{plan.title}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{plan.status === "done" ? "✅ Completado" : `${plan.done}/${plan.total}`}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{item.kind === "tema" ? "🧵 Tema" : "💬 Chat principal"}: {item.conversation_title}{here ? " (esta conversación)" : ""}</span>
                  <span className="shrink-0">{fmtDate(plan.updated_at)}</span>
                </div>
                <div role="progressbar" aria-valuemin={0} aria-valuemax={plan.total} aria-valuenow={plan.done} className="h-1.5 w-full rounded-full bg-foreground/10 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${plan.total ? Math.round((100 * plan.done) / plan.total) : 0}%` }} />
                </div>
                {plan.stale_days ? <p data-testid={`project-plan-stale-${item.conversation_id}`} className="text-xs text-amber-500">Lleva {plan.stale_days} día(s) sin moverse.</p> : null}
              </button>
              {open && (
                <div className="space-y-2 pl-1">
                  {plan.goal && <p className="text-xs text-muted-foreground">{plan.goal}</p>}
                  <Steps steps={plan.steps} testPrefix={`project-plan-${item.conversation_id}`} showMissing />
                  {!here && onOpenConversation && (
                    <Button data-testid={`project-plan-go-${item.conversation_id}`} size="sm" variant="outline" onClick={() => { onOpenConversation(item.conversation_id); onClose(); }}>Ir a esta conversación</Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PlanDialog({ open, onOpenChange, conversationId, data, working = false, projectId = null, onOpenConversation = null }) {
  const archive = useArchivePlan(conversationId);
  const edit = useEditPlan(conversationId);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const [openHistory, setOpenHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [newStep, setNewStep] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [goalDraft, setGoalDraft] = useState("");
  const current = data?.current;
  const history = data?.history || [];
  useEffect(() => { setTitleDraft(current?.title || ""); setGoalDraft(current?.goal || ""); }, [current?.title, current?.goal]);
  useEffect(() => { if (!current) setEditing(false); }, [current]);
  useEffect(() => { if (!open) setShowAll(false); }, [open]);

  // Una edición manual. `base` = la versión que el usuario está viendo: si el plan cambió, el servidor lo rechaza y aquí se vuelve a leer.
  const run = async (body) => {
    setError("");
    try {
      await edit.mutateAsync({ ...body, base: current?.updated_at });
      return true;
    } catch (e) {
      setError(errorMessage(e, "No se pudo guardar el cambio."));
      return false;
    }
  };
  const addStep = async () => {
    const text = newStep.trim();
    if (text && (await run({ op: "agregar", text }))) setNewStep("");
  };
  const controls = {
    working, editing, busy: edit.isPending,
    onState: (s, state) => run({ op: "estado", step_id: s.id, state }),
    onText: (s, text) => run({ op: "texto", step_id: s.id, text }),
    onMove: (s, direction) => run({ op: "mover", step_id: s.id, direction }),
    onRemove: (s) => run({ op: "quitar", step_id: s.id }),
    onEvidenceAdd: (s, ev) => run({ op: "evidencia", step_id: s.id, kind: ev.kind, value: ev.value, label: ev.label }),
    onEvidenceRemove: (s, e) => run({ op: "quitar_evidencia", step_id: s.id, evidence_id: e.id }),
  };
  const saveTitle = () => {
    const title = titleDraft.trim();
    if (!title) setTitleDraft(current.title);
    else if (title !== current.title) run({ op: "titulo", title });
  };
  const saveGoal = () => { if (goalDraft.trim() !== (current.goal || "")) run({ op: "titulo", goal: goalDraft }); };

  const doArchive = async () => {
    setError("");
    try {
      await archive.mutateAsync();
      setConfirm(false);
    } catch (e) {
      setError(errorMessage(e, "No se pudo archivar el plan."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plan-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{showAll ? "📚 Planes del proyecto" : "📋 Plan de esta conversación"}</DialogTitle>
          <DialogDescription>{showAll ? "El plan vigente de cada conversación y cada tema, con su avance." : "Lo que el Orquestador y tú acordaron hacer, dónde vamos y qué ya se hizo. Cada conversación y cada tema tiene el suyo."}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-4">
          {showAll && (
            <>
              <button type="button" data-testid="project-plans-back" className="text-xs underline" onClick={() => setShowAll(false)}>← Plan de esta conversación</button>
              <ProjectPlans projectId={projectId} conversationId={conversationId} onOpenConversation={onOpenConversation} onClose={() => onOpenChange(false)} />
            </>
          )}
          {!showAll && !current && (
            <p data-testid="plan-empty" className="text-sm text-muted-foreground">
              Todavía no hay un plan en esta conversación. Cuando le pidas un plan al Orquestador (o él proponga uno de varios pasos), aparecerá aquí con su avance.
            </p>
          )}

          {!showAll && current && (
            <div data-testid="plan-current" className="space-y-3">
              {editing && !working ? (
                <div className="space-y-2">
                  <Input data-testid="plan-title-input" value={titleDraft} maxLength={100} disabled={edit.isPending} aria-label="Título del plan" className="font-semibold"
                    onChange={(e) => setTitleDraft(e.target.value)} onBlur={saveTitle} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
                  <Input data-testid="plan-goal-input" value={goalDraft} maxLength={240} disabled={edit.isPending} aria-label="Objetivo del plan" placeholder="Objetivo (opcional)" className="text-sm"
                    onChange={(e) => setGoalDraft(e.target.value)} onBlur={saveGoal} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
                </div>
              ) : (
                <div>
                  <h3 data-testid="plan-title" className="text-lg font-semibold leading-snug">{current.title}</h3>
                  {current.goal && <p data-testid="plan-goal" className="text-sm text-muted-foreground">{current.goal}</p>}
                </div>
              )}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span data-testid="plan-progress-text">{current.done} de {current.total} pasos</span>
                  <span data-testid="plan-status">{current.status === "done" ? "✅ Completado" : "En curso"}</span>
                </div>
                <div role="progressbar" aria-valuemin={0} aria-valuemax={current.total} aria-valuenow={current.done} className="h-1.5 w-full rounded-full bg-foreground/10 overflow-hidden">
                  <div data-testid="plan-bar" className="h-full bg-emerald-500" style={{ width: `${current.total ? Math.round((100 * current.done) / current.total) : 0}%` }} />
                </div>
              </div>
              {current.stale_days ? (
                <p data-testid="plan-stale" className="text-xs text-amber-500">Este plan lleva {current.stale_days} día(s) sin moverse. Si ya no es el vigente, archívalo.</p>
              ) : null}
              <Steps steps={current.steps} testPrefix="plan" controls={controls} showMissing />
              {editing && !working && (
                current.total < MAX_STEPS ? (
                  <div className="flex items-center gap-2">
                    <Input data-testid="plan-add-input" value={newStep} maxLength={120} disabled={edit.isPending} placeholder="Nuevo paso…" aria-label="Nuevo paso" className="h-8 text-sm"
                      onChange={(e) => setNewStep(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addStep(); }} />
                    <Button data-testid="plan-add" size="sm" variant="outline" disabled={edit.isPending || !newStep.trim()} onClick={addStep}>➕ Agregar</Button>
                  </div>
                ) : (
                  <p data-testid="plan-add-max" className="text-xs text-muted-foreground">El plan ya tiene {MAX_STEPS} pasos, que es el máximo.</p>
                )
              )}
              <p className="text-xs text-muted-foreground">Creado el {fmtDate(current.created_at)} · actualizado el {fmtDate(current.updated_at)}</p>
              {working ? (
                <p data-testid="plan-working" className="text-xs text-muted-foreground">El Orquestador está trabajando en esta conversación. Podrás editar el plan cuando termine.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="plan-edit-toggle" className="text-xs underline" onClick={() => { setError(""); setEditing((v) => !v); }}>{editing ? "✔ Listo" : "✏️ Editar plan"}</button>
                  {!editing && <span className="text-xs text-muted-foreground">Pulsa el ícono de un paso para cambiar su estado.</span>}
                </div>
              )}

              {!confirm ? (
                <button type="button" data-testid="plan-archive" className="text-xs underline text-muted-foreground" onClick={() => setConfirm(true)}>Archivar este plan</button>
              ) : (
                <div data-testid="plan-archive-confirm" className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-xs">El plan pasa al historial y el Orquestador podrá empezar uno nuevo. No se borra nada.</p>
                  <div className="flex gap-2">
                    <Button data-testid="plan-archive-yes" size="sm" variant="outline" onClick={doArchive} disabled={archive.isPending}>Sí, archivar</Button>
                    <Button data-testid="plan-archive-no" size="sm" variant="outline" onClick={() => setConfirm(false)}>No</Button>
                  </div>
                </div>
              )}
              {error && <p data-testid="plan-error" className="text-xs text-amber-500">{error}</p>}
            </div>
          )}

          {!showAll && history.length > 0 && (
            <div data-testid="plan-history" className="space-y-2 border-t border-border pt-3">
              <button type="button" data-testid="plan-history-toggle" className="text-sm font-medium" onClick={() => setShowHistory((v) => !v)}>
                {showHistory ? "▾" : "▸"} Planes anteriores ({history.length})
              </button>
              {showHistory && (
                <ul className="space-y-2">
                  {history.map((h, i) => (
                    <li key={h.id} data-testid={`plan-history-${i}`}>
                      <button type="button" className="w-full text-left text-sm flex items-center justify-between gap-2" onClick={() => setOpenHistory(openHistory === h.id ? null : h.id)}>
                        <span className="truncate">{h.title}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{h.done} de {h.total} · {fmtDate(h.updated_at)}</span>
                      </button>
                      {openHistory === h.id && <div className="mt-2 pl-2"><Steps steps={h.steps} testPrefix={`plan-history-${i}`} /></div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!showAll && projectId && (
            <div className="border-t border-border pt-3">
              <button type="button" data-testid="project-plans-open" className="text-sm underline" onClick={() => setShowAll(true)}>📚 Todos los planes del proyecto</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
